import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {Const, ResourceState} from '../../../core/helpers/Const';
import {ObjectStatus} from '@fundamental-ngx/core';
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import {Utils} from "../../../core/helpers/Utils";
import {ResourceModel} from "../../../core/models/resources/resource.model";

@Component({
    selector: 'app-status',
    templateUrl: './status.component.html',
    styleUrls: ['./status.component.scss'],
    standalone: false
})
export class StatusComponent implements OnChanges {
	@Input() status: ResourceState | null;
	@Input() showStatusText = false;
	@Input() withConditionsTable = false;
	@Input() customTooltip: string;
	/** When set with a resource, shows an inline "Analyze Failure" AI action on hover for failed states. */
	@Input() analyzable = false;
	@Input() resource: ResourceModel;
	@Output() analyzeFailure = new EventEmitter<ResourceModel>();

	currentStatus: ObjectStatus;
	stateText: string;
	fontAwesomeGlyph: IconProp = 'minus';
	isStatusUnstable: boolean;
	statusTooltip: string;

	ngOnChanges(changes: SimpleChanges) {
	    let status = changes['status'];
	    if (status) {
	        let currentStatus = changes['status'].currentValue;
	        this.currentStatus = this.getStatus(currentStatus);
	        this.stateText = this.getStateText(currentStatus);
	        this.fontAwesomeGlyph = StatusComponent.getFontAwesomeStateGlyph(currentStatus);
	        this.isStatusUnstable = this.isUnstable(currentStatus);
	        this.statusTooltip = this.customTooltip ? this.customTooltip : StatusComponent.getStatusTooltip(currentStatus, this.showStatusText);
	    }
	}

	static getStatusTooltip(state: ResourceState | null, showStatusText: boolean) : string {
	    switch (state) {
	    case ResourceState.READY:
	        return $localize`:XTOL|Tooltip for the resource status ready@@resource.status.tooltip.ready:Resource is ready`;
	    case ResourceState.FALLBACK_READY:
	        return $localize`:XTOL|Tooltip for the resource status fallback_ready@@resource.status.tooltip.fallbackready:The resource's Ready condition is True and the resource's actual state has reached its desired state.`;
	    case ResourceState.TERMINATING:
	        return $localize`:XTOL|Tooltip for the resource status termination@@resource.status.tooltip.termination:Resource is being deleted, including related resources. This could take a while.`;
	    case ResourceState.FAILED:
	        return $localize`:XTOL|Tooltip for the resource status failed@@resource.status.tooltip.failed:Failed to process this resource`;
	    case ResourceState.TERMINATION_FAILED:
	        return $localize`:XTOL|Tooltip for the resource status termination_failed@@resource.status.tooltip.termination_failed:Failed to complete the termination of this resource`;
	    case ResourceState.NOT_READY:
	        return $localize`:XTOL|Tooltip for the resource status notready@@resource.status.tooltip.notready:The resource's Ready condition is False and the observed generation has reached the resource's generation.`;
	    case ResourceState.IN_PROCESS:
	        return $localize`:XTOL|Tooltip for the resource status inprocess@@resource.status.tooltip.inprocess:Resource is being processed`;
	    case ResourceState.FALLBACK_TERMINATING:
	        return $localize`:XTOL|Tooltip for the resource status fallback_terminating@@resource.status.tooltip.fallbackterminating:The resource is pending deletion.`;
	    case ResourceState.UNKNOWN:
	        return $localize`:XTOL|Tooltip for the resource status unknown@@resource.status.tooltip.unknown:The resource's Ready condition is not known or missing.`;
	    }
	    return "";
	}

	getStatus(state: ResourceState | null): ObjectStatus {
	    if (!state) {
	        return 'critical';
	    }
	    return Const.OBJECT_STATUS[state];
	}

	static getFontAwesomeStateGlyph(state: ResourceState | null) : IconProp {
	    if (!state) {
	        return Const.FONT_AWESOME_STATE_GLYPHS[ResourceState.IN_PROCESS];
	    }
	    return Const.FONT_AWESOME_STATE_GLYPHS[state] || ['fas', 'puzzle-piece'];
	}

	getStateText(state: ResourceState | null) {
	    if (!state) {
	        return '';
	    }
	    return Const.STATUS_DISPLAY_NAMES[state];
	}

	isUnstable(state: ResourceState | null): boolean {
	    if (!state) {
	        return true;
	    }
	    return [ResourceState.IN_PROCESS, ResourceState.TERMINATING].includes(state);
	}

	/** Show the inline AI action only when opted in, AI is available, and the state is a failure. */
	get showAnalyze(): boolean {
	    return this.analyzable
			&& !!this.resource
			&& Utils.isAiAssistantAvailable()
			&& [ResourceState.FAILED, ResourceState.TERMINATION_FAILED, ResourceState.NOT_READY].includes(this.status);
	}

	onAnalyze(event: Event): void {
	    event.stopPropagation();
	    this.analyzeFailure.emit(this.resource);
	}
}
